/**
 * @fileoverview Upgrade to ONE_SHOT Modal component.
 * @summary Modal for confirming event upgrade to ONE_SHOT plan.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/UpgradeOneShotModal
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faBolt, faCheckCircle, faInfoCircle, faClock } from '@fortawesome/free-solid-svg-icons'
import { SUBSCRIPTION_PLANS, SubscriptionType } from '@karaonow/shared'
import Spinner from '../../../components/Spinner'
import type { FC } from 'react'

interface UpgradeOneShotModalProps {
  eventName: string
  isOpen: boolean
  isLoading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * @summary Modal for confirming ONE_SHOT event upgrade.
 * @param {UpgradeOneShotModalProps} props - Component props
 * @returns {ReactElement | null} Upgrade confirmation modal or null if not open
 */
const UpgradeOneShotModal: FC<UpgradeOneShotModalProps> = ({
  eventName,
  isOpen,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  const oneShotPlan = SUBSCRIPTION_PLANS[SubscriptionType.ONE_SHOT]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-2 pt-16 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 sm:p-6  shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 shrink-0">
              <FontAwesomeIcon icon={faBolt} className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              {t('subscription.upgradeToUnlimited') || '🚀 Passer en illimité'}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0"
            aria-label={t('common.close')}
          >
            <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              {t('subscription.upgradeSingleEventConfirm', { eventName }) || 
                `Vous êtes sur le point de passer l'événement "${eventName}" en illimité.`}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('subscription.singleEventDescription') || 
                'Il s\'agit d\'un paiement unique pour un nombre illimité de chansons dans cet événement spécifique.'}
            </p>
          </div>

          {/* Price Display */}
          <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('subscription.price') || 'Price'}
              </span>
              <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                €{oneShotPlan.priceEur?.toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {t('subscription.oneTimePayment') || 'One-time payment'}
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('subscription.whatYouGet') || "What you'll get:"}
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                <span>{t('subscription.unlimitedSongs') || 'Unlimited songs for this event'}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                <span>{t('subscription.noLimits') || 'No queue limits'}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                <span>{t('subscription.oneShotValidity') || 'Valid until event completion'}</span>
              </li>
            </ul>
          </div>

          {/* Validity Alert */}
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-2">
              <FontAwesomeIcon icon={faClock} className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-amber-900 dark:text-amber-200 font-medium">
                  {t('subscription.oneShotValidityInfo', { days: 15 }) ||
                    'Cet achat est valable 15 jours à partir de la date d\'achat. Utilisez-le avant expiration !'}
                </p>
              </div>
            </div>
          </div>

          {/* Premium Suggestion */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <FontAwesomeIcon icon={faInfoCircle} className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-blue-900 dark:text-blue-200 mb-2">
                  {t('subscription.multiplePremiumSuggestion') || 
                    'Multiple events? The Premium plan might be more cost-effective.'}
                </p>
                <Link
                  to="/subscription"
                  className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                >
                  {t('subscription.comparePlans') || 'Compare plans →'}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 shrink-0 bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="btn btn-secondary order-2 sm:order-1"
          >
            {t('common.cancel') || 'Annuler'}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="btn btn-primary flex items-center justify-center gap-2 order-1 sm:order-2"
          >
            {isLoading ? (
              <>
                <Spinner size="sm" variant="white" />
                <span>{t('common.processing') || 'Traitement'}...</span>
              </>
            ) : (
              <span>{t('subscription.upgradeToUnlimitedNow') || '🚀 Passer en illimité maintenant'}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UpgradeOneShotModal

